import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useSearchWithDebounce } from '../hooks/useSearchWithDebounce';
import { cn } from '../lib/utils';

interface SearchInputProps {
  placeholder?: string;
  className?: string;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  onSearch: (value: string) => void;
  debounceDelay?: number;
  initialValue?: string;
  showClearButton?: boolean;
}

const widthClasses = {
  sm: 'w-64', // 256px
  md: 'w-72', // 288px
  lg: 'w-80', // 320px
  xl: 'w-96', // 384px
};

/**
 * Reusable search input component with debounce functionality
 *
 * @param placeholder - Placeholder text for the input
 * @param className - Additional CSS classes
 * @param width - Predefined width size
 * @param onSearch - Callback function when search value changes (debounced)
 * @param debounceDelay - Debounce delay in milliseconds
 * @param initialValue - Initial value for the input
 * @param showClearButton - Whether to show clear button when input has value
 */
export const SearchInput = ({
  placeholder,
  className = '',
  width = 'lg',
  onSearch,
  debounceDelay,
  initialValue = '',
  showClearButton = true,
}: SearchInputProps) => {
  const { t } = useTranslation('shared');
  const { inputValue, setInputValue, clearSearch } = useSearchWithDebounce(onSearch, {
    delay: debounceDelay,
    initialValue,
  });
  const effectivePlaceholder = placeholder ?? t('search.placeholder');

  return (
    <div className={cn('relative', widthClasses[width], className)}>
      {/* Search Icon */}
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />

      {/* Input Field */}
      <Input
        type="text"
        placeholder={effectivePlaceholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        className={cn('pl-10', showClearButton && inputValue ? 'pr-10' : '')}
      />

      {/* Clear Button */}
      {showClearButton && inputValue && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
          onClick={clearSearch}
        >
          <X className="h-4 w-4 text-gray-400" />
        </Button>
      )}
    </div>
  );
};

export default SearchInput;
